import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, rupiah, tanggal } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { SiteLayout } from "@/site/SiteLayout";
import { Loading, Empty } from "@/site/parts";
import { toast } from "sonner";
import { Check, Plus, Trash2, ArrowRight, ArrowLeft, Copy, LogIn, UserCheck, X, UserPlus } from "lucide-react";

const inp = "w-full rounded-xl border border-[#E5DEC9] px-4 py-3 text-sm outline-none focus:border-[#C5A028]";
const lbl = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

const emptyPilgrim = () => ({
  uid: `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  full_name_passport: "", nik: "", gender: "L", birth_place: "", birth_date: "",
  phone: "", email: "", address: "", emergency_contact_name: "", emergency_contact_phone: "",
  passport_number: "", passport_expiry: "", relation_to_customer: "diri sendiri",
  special_needs: "", room_type: "Quad",
});

const STEPS = ["Jadwal & Kamar", "Data Pemesan", "Data Jamaah", "Referral", "Konfirmasi"];

export default function BookingWizard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    departure_id: "", room_type: "Quad", contact_name: "", contact_email: "",
    contact_phone: "", contact_address: "", referral_code: "", note: "",
  });
  const [pilgrims, setPilgrims] = useState([emptyPilgrim()]);
  const [refInfo, setRefInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authAfterSubmit, setAuthAfterSubmit] = useState(false);
  const authAutoSubmitRef = useRef(false);

  useEffect(() => {
    api.get(`/public/products/${slug}`).then((r) => {
      setProduct(r.data);
      setForm((f) => ({ ...f, departure_id: r.data.departures?.[0]?.id || "" }));
    }).catch((e) => toast.error(apiError(e)));
    const ref = new URLSearchParams(window.location.search).get("ref")
      || localStorage.getItem("rsh_ref") || "";
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [slug]);

  useEffect(() => {
    if (user) {
      const addr = [user.address_street, user.address_city].filter(Boolean).join(", ");
      setForm((f) => ({
        ...f,
        contact_name:    f.contact_name    || user.full_name || "",
        contact_email:   f.contact_email   || user.email    || "",
        contact_phone:   f.contact_phone   || user.phone    || "",
        contact_address: f.contact_address || addr          || "",
      }));
    }
  }, [user]);

  const openAuth = (mode = "login", afterSubmit = false) => {
    authAutoSubmitRef.current = afterSubmit;
    setAuthMode(mode);
    setAuthAfterSubmit(afterSubmit);
    setShowAuth(true);
  };

  const dep = product?.departures?.find((d) => d.id === form.departure_id);
  const price = dep?.prices?.find((p) => p.room_type === form.room_type)?.price
    || product?.promo_price || product?.base_price || 0;
  const total = price * pilgrims.length;

  const checkRef = async () => {
    if (!form.referral_code) return setRefInfo(null);
    try {
      const { data } = await api.get(`/public/referral/${form.referral_code}`);
      setRefInfo(data);
      toast.success(`Referral valid: ${data.partner_name}`);
    } catch (e) {
      setRefInfo(false);
      toast.error(apiError(e));
    }
  };

  const createBooking = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        product_id: product.id, departure_id: form.departure_id || null,
        contact_name: form.contact_name, contact_email: form.contact_email,
        contact_phone: form.contact_phone, contact_address: form.contact_address,
        room_type: form.room_type, referral_code: form.referral_code, note: form.note,
        pilgrims: pilgrims.map(({ uid, ...rest }) => rest),
      });
      setResult(data);
      setStep(5);
      toast.success(`Booking ${data.booking.booking_code} dibuat`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }, [form, pilgrims, product]);

  useEffect(() => {
    if (user && authAfterSubmit && authAutoSubmitRef.current && !result && product) {
      authAutoSubmitRef.current = false;
      setAuthAfterSubmit(false);
      createBooking();
    }
  }, [user, authAfterSubmit, result, product, createBooking]);

  const submit = async () => {
    if (!user) {
      localStorage.setItem("rsh_ref", form.referral_code || "");
      return openAuth("register", true);
    }
    return createBooking();
  };

  if (!product) return <SiteLayout><Loading /></SiteLayout>;

  if (result) {
    return (
      <SiteLayout>
        <div className="mx-auto max-w-3xl px-6 py-16" data-testid="booking-success">
          <div className="rounded-2xl border border-[#E5DEC9] bg-white p-8 luxury-shadow">
            <Check className="text-[#15803D]" size={34} />
            <h1 className="mt-4 font-display text-3xl font-bold text-[#141414]">Booking Berhasil Dibuat</h1>
            <p className="mt-2 text-sm text-[#4A4844]">Simpan kode booking Anda dan lakukan pembayaran manual.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Info k="Kode Booking" v={result.booking.booking_code} testid="result-booking-code" />
              <Info k="Nomor Invoice" v={result.invoice.invoice_number} />
              <Info k="Jumlah Jamaah" v={`${result.booking.pax} orang`} />
              <Info k="Total Tagihan" v={rupiah(result.invoice.total)} testid="result-total" />
            </div>
            <div className="mt-6 rounded-xl bg-[#F4EFE6] p-5">
              <div className="eyebrow">Instruksi Pembayaran Manual</div>
              <p className="mt-2 text-xs text-[#4A4844]">Cicilan bebas: transfer berapa kali pun sesuai kemampuan hingga lunas.</p>
              <ul className="mt-3 space-y-2 text-sm" data-testid="payment-instructions">
                {result.payment_instructions.map((b) => (
                  <li key={b.id} className="rounded-lg border border-[#E5DEC9] bg-white p-3">
                    <b>{b.bank_name}</b> — {b.account_number} a.n. {b.account_holder}
                  </li>))}
              </ul>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/portal/tagihan" data-testid="goto-tagihan" className="btn-gold rounded-xl px-6 py-3 text-sm font-bold">Upload Bukti Bayar</Link>
              <Link to="/portal/booking" className="rounded-xl border border-[#141414] px-6 py-3 text-sm font-semibold text-[#141414]">Booking Saya</Link>
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="eyebrow">Pendaftaran</div>
        <h1 className="mt-2 font-display text-3xl font-bold text-[#141414]">{product.name}</h1>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="booking-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`rounded-full px-3 py-1.5 text-xs font-bold ${i === step ? "bg-[#141414] text-white" : i < step ? "bg-[#F5F1E8] text-[#15803D]" : "border border-[#E5DEC9] text-[#8A867C]"}`}>
              {i + 1}. {s}
            </div>))}
        </div>

        <div className="mt-8 rounded-2xl border border-[#E5DEC9] bg-white p-7">
          {step === 0 && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="step-jadwal">
              <div className="sm:col-span-2"><label className={lbl}>Jadwal Keberangkatan</label>
                <select className={inp} data-testid="bw-departure" value={form.departure_id}
                  onChange={(e) => setForm({ ...form, departure_id: e.target.value })}>
                  {(product.departures || []).length === 0 && <option value="">Tanpa jadwal tetap</option>}
                  {(product.departures || []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {tanggal(d.depart_date)} — sisa {d.seats_total - d.seats_booked} kursi
                    </option>))}
                </select></div>
              <div><label className={lbl}>Tipe Kamar</label>
                <select className={inp} data-testid="bw-room" value={form.room_type}
                  onChange={(e) => setForm({ ...form, room_type: e.target.value })}>
                  {(product.room_types || ["Quad"]).map((r) => <option key={r} value={r}>{r}</option>)}
                </select></div>
              <div><label className={lbl}>Harga per Jamaah</label>
                <div className="rounded-xl bg-[#F4EFE6] px-4 py-3 text-sm font-bold text-[#141414]" data-testid="bw-price">{rupiah(price)}</div></div>
            </div>)}

          {step === 1 && (
            <div className="space-y-4" data-testid="step-pemesan">
              {/* Banner login / status akun */}
              {!user ? (
                <div className="flex items-center justify-between rounded-xl border border-[#E5DEC9] bg-[#FFFDF5] px-4 py-3" data-testid="login-banner">
                  <div className="text-sm text-[#4A4844]">
                    <span className="font-semibold text-[#141414]">Sudah punya akun?</span> Login untuk mengisi data otomatis.
                  </div>
                  <button onClick={() => openAuth("login", false)} data-testid="login-from-booking"
                    className="flex items-center gap-2 rounded-lg bg-[#141414] px-4 py-2 text-xs font-bold text-white hover:bg-[#333]">
                    <LogIn size={13} /> Masuk
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3" data-testid="autofill-banner">
                  <UserCheck size={18} className="shrink-0 text-green-700" />
                  <div className="flex-1 text-sm">
                    <span className="font-semibold text-green-800">Data diisi otomatis dari akun Anda</span>
                    <span className="ml-2 text-green-700">({user.email})</span>
                  </div>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={lbl}>Nama Pemesan</label><input className={inp} data-testid="bw-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
                <div><label className={lbl}>Email</label><input className={inp} type="email" data-testid="bw-contact-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
                <div><label className={lbl}>Nomor WhatsApp</label><input className={inp} data-testid="bw-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
                <div><label className={lbl}>Alamat</label><input className={inp} data-testid="bw-contact-address" value={form.contact_address} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} /></div>
              </div>
            </div>)}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-jamaah">
              {pilgrims.map((p, i) => (
                <div key={p.uid} className="rounded-xl border border-[#E5DEC9] p-5" data-testid={`pilgrim-form-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="eyebrow">Jamaah {i + 1}</div>
                    {pilgrims.length > 1 && (
                      <button onClick={() => setPilgrims(pilgrims.filter((_, x) => x !== i))}
                        data-testid={`pilgrim-remove-${i}`} className="text-[#B91C1C]"><Trash2 size={15} /></button>)}
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {[["full_name_passport", "Nama sesuai paspor", "text"],
                      ["nik", "NIK", "text"], ["birth_place", "Tempat lahir", "text"],
                      ["birth_date", "Tanggal lahir", "date"], ["phone", "Nomor HP", "text"],
                      ["email", "Email", "email"], ["passport_number", "Nomor paspor", "text"],
                      ["passport_expiry", "Masa berlaku paspor", "date"],
                      ["emergency_contact_name", "Kontak darurat (nama)", "text"],
                      ["emergency_contact_phone", "Kontak darurat (telepon)", "text"]].map(([k, label, t]) => (
                      <div key={k}><label className={lbl}>{label}</label>
                        <input className={inp} type={t} data-testid={`pf-${i}-${k}`} value={p[k]}
                          onChange={(e) => { const n = [...pilgrims]; n[i] = { ...p, [k]: e.target.value }; setPilgrims(n); }} /></div>))}
                    <div><label className={lbl}>Jenis kelamin</label>
                      <select className={inp} data-testid={`pf-${i}-gender`} value={p.gender}
                        onChange={(e) => { const n = [...pilgrims]; n[i] = { ...p, gender: e.target.value }; setPilgrims(n); }}>
                        <option value="L">Laki-laki</option><option value="P">Perempuan</option>
                      </select></div>
                    <div><label className={lbl}>Hubungan dengan pemesan</label>
                      <input className={inp} data-testid={`pf-${i}-relation`} value={p.relation_to_customer}
                        onChange={(e) => { const n = [...pilgrims]; n[i] = { ...p, relation_to_customer: e.target.value }; setPilgrims(n); }} /></div>
                    <div className="sm:col-span-2"><label className={lbl}>Alamat</label>
                      <input className={inp} data-testid={`pf-${i}-address`} value={p.address}
                        onChange={(e) => { const n = [...pilgrims]; n[i] = { ...p, address: e.target.value }; setPilgrims(n); }} /></div>
                    <div className="sm:col-span-2"><label className={lbl}>Kebutuhan khusus (opsional)</label>
                      <input className={inp} data-testid={`pf-${i}-special`} value={p.special_needs}
                        onChange={(e) => { const n = [...pilgrims]; n[i] = { ...p, special_needs: e.target.value }; setPilgrims(n); }} /></div>
                  </div>
                </div>))}
              <button onClick={() => setPilgrims([...pilgrims, emptyPilgrim()])} data-testid="pilgrim-add"
                className="flex items-center gap-2 rounded-xl border border-[#141414] px-4 py-2.5 text-sm font-semibold text-[#141414]">
                <Plus size={15} /> Tambah Jamaah
              </button>
            </div>)}

          {step === 3 && (
            <div className="max-w-md space-y-4" data-testid="step-referral">
              <div><label className={lbl}>Kode Referral Mitra (opsional)</label>
                <input className={inp} data-testid="bw-referral" value={form.referral_code}
                  onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  placeholder="misal UC-XXXXXX" /></div>
              <button onClick={checkRef} data-testid="bw-referral-check"
                className="rounded-xl border border-[#141414] px-4 py-2.5 text-sm font-semibold text-[#141414]">Validasi Kode</button>
              {refInfo && (
                <div className="rounded-xl bg-[#F5F1E8] p-4 text-sm" data-testid="referral-valid">
                  Mitra: <b>{refInfo.partner_name}</b><br />Cabang: {refInfo.branch_name}
                  {refInfo.subbranch_name && <> — Cab. Pembantu: {refInfo.subbranch_name}</>}
                </div>)}
              {refInfo === false && <div className="rounded-xl bg-[#FBE9E9] p-4 text-sm text-[#B91C1C]" data-testid="referral-invalid">Kode referral tidak valid.</div>}
              <p className="text-xs text-[#8A867C]">Tanpa referral, booking tetap dapat diproses namun tidak menghasilkan komisi mitra.</p>
            </div>)}

          {step === 4 && (
            <div className="space-y-4" data-testid="step-konfirmasi">
              <div className="grid gap-3 sm:grid-cols-2">
                <Info k="Paket" v={product.name} />
                <Info k="Keberangkatan" v={dep ? tanggal(dep.depart_date) : "-"} />
                <Info k="Tipe Kamar" v={form.room_type} />
                <Info k="Jumlah Jamaah" v={`${pilgrims.length} orang`} />
                <Info k="Harga per Jamaah" v={rupiah(price)} />
                <Info k="Total Tagihan" v={rupiah(total)} testid="confirm-total" />
                <Info k="Pemesan" v={`${form.contact_name} — ${form.contact_email}`} />
                <Info k="Referral" v={form.referral_code || "Tanpa referral"} />
              </div>
              {!user && <div className="rounded-xl bg-[#FDF6E3] p-4 text-sm text-[#8C6D1F]" data-testid="booking-inline-auth-notice">Akun diperlukan untuk menyimpan booking dan invoice. Anda bisa masuk atau daftar tanpa meninggalkan halaman ini.</div>}
            </div>)}

          <div className="mt-8 flex items-center justify-between">
            <button disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="bw-prev"
              className="flex items-center gap-2 rounded-xl border border-[#E5DEC9] px-5 py-2.5 text-sm font-semibold disabled:opacity-40">
              <ArrowLeft size={15} /> Sebelumnya
            </button>
            {step < 4 ? (
              <button onClick={() => {
                if (step === 1 && (!form.contact_name || !form.contact_email || !form.contact_phone))
                  return toast.error("Lengkapi data pemesan");
                if (step === 2 && pilgrims.some((p) => !p.full_name_passport))
                  return toast.error("Nama sesuai paspor wajib diisi untuk semua jamaah");
                setStep(step + 1);
              }} data-testid="bw-next" className="btn-gold flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold">
                Lanjut <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={submit} disabled={busy} data-testid="bw-submit"
                className="btn-gold rounded-xl px-6 py-3 text-sm font-bold">
                {busy ? "Memproses..." : user ? "Buat Booking & Invoice" : "Masuk/Daftar & Buat Invoice"}
              </button>)}
          </div>
        </div>
      </div>
      {showAuth && (
        <BookingAuthModal
          mode={authMode}
          onModeChange={setAuthMode}
          onClose={() => { authAutoSubmitRef.current = false; setShowAuth(false); setAuthAfterSubmit(false); }}
          onSuccess={() => setShowAuth(false)}
          initialData={form}
        />
      )}
    </SiteLayout>
  );
}

function BookingAuthModal({
  mode, onModeChange, onClose, onSuccess, initialData,
  contextLabel = "Lanjutkan Booking",
  description = "Data paket dan jamaah tetap tersimpan di halaman ini.",
  registerSuccessMessage = "Akun berhasil dibuat. Booking dilanjutkan di halaman ini.",
}) {
  const { login, register } = useAuth();
  const initialName = initialData.contact_name || initialData.recipient_name || "";
  const initialEmail = initialData.contact_email || initialData.email || "";
  const initialPhone = initialData.contact_phone || initialData.recipient_phone || "";
  const [loginForm, setLoginForm] = useState({ email: initialEmail, password: "" });
  const [registerForm, setRegisterForm] = useState({
    full_name: initialName,
    email: initialEmail,
    phone: initialPhone,
    password: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success(`Selamat datang, ${u.full_name}`);
      onSuccess();
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault(); setErr("");
    if (registerForm.password.length < 8) { setErr("Password minimal 8 karakter"); return; }
    setBusy(true);
    try {
      await register(registerForm);
      toast.success(registerSuccessMessage);
      onSuccess();
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 p-4" data-testid="booking-auth-modal">
      <div className="my-8 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="eyebrow">{contextLabel}</div>
            <h2 className="mt-1 font-display text-2xl font-bold text-[#141414]">
              {mode === "login" ? "Masuk tanpa kehilangan data" : "Daftar tanpa mengulang form"}
            </h2>
            <p className="mt-2 text-sm text-[#4A4844]">{description}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="booking-auth-close" className="rounded-lg p-2 hover:bg-[#F4EFE6]">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 rounded-xl bg-[#F4EFE6] p-1" data-testid="booking-auth-tabs">
          <button type="button" onClick={() => { setErr(""); onModeChange("login"); }} data-testid="booking-auth-tab-login"
            className={`rounded-lg px-3 py-2 text-sm font-bold ${mode === "login" ? "bg-[#141414] text-white" : "text-[#4A4844]"}`}>
            Masuk
          </button>
          <button type="button" onClick={() => { setErr(""); onModeChange("register"); }} data-testid="booking-auth-tab-register"
            className={`rounded-lg px-3 py-2 text-sm font-bold ${mode === "register" ? "bg-[#141414] text-white" : "text-[#4A4844]"}`}>
            Daftar
          </button>
        </div>

        {mode === "login" ? (
          <form onSubmit={submitLogin} className="mt-5 space-y-3" data-testid="booking-login-form">
            <div><label className={lbl}>Email</label><input required type="email" className={inp} data-testid="booking-login-email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} /></div>
            <div><label className={lbl}>Password</label><input required type="password" className={inp} data-testid="booking-login-password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} /></div>
            {err && <p data-testid="booking-auth-error" className="rounded-lg bg-[#FBE9E9] px-3 py-2 text-sm text-[#B91C1C]">{err}</p>}
            <button disabled={busy} data-testid="booking-login-submit" className="btn-gold flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold">
              <LogIn size={15} /> {busy ? "Memproses..." : "Masuk & Lanjutkan"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitRegister} className="mt-5 space-y-3" data-testid="booking-register-form">
            <div><label className={lbl}>Nama lengkap</label><input required className={inp} data-testid="booking-register-name" value={registerForm.full_name} onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })} /></div>
            <div><label className={lbl}>Email</label><input required type="email" className={inp} data-testid="booking-register-email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} /></div>
            <div><label className={lbl}>Nomor WhatsApp</label><input className={inp} data-testid="booking-register-phone" value={registerForm.phone} onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })} /></div>
            <div><label className={lbl}>Password</label><input required type="password" className={inp} data-testid="booking-register-password" value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} /></div>
            {err && <p data-testid="booking-auth-error" className="rounded-lg bg-[#FBE9E9] px-3 py-2 text-sm text-[#B91C1C]">{err}</p>}
            <button disabled={busy} data-testid="booking-register-submit" className="btn-gold flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold">
              <UserPlus size={15} /> {busy ? "Memproses..." : "Daftar & Lanjutkan"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export const Info = ({ k, v, testid }) => (
  <div className="rounded-xl border border-[#E5DEC9] p-4">
    <div className="eyebrow">{k}</div>
    <div className="mt-1 text-sm font-semibold text-[#121212]" data-testid={testid}>{v}</div>
  </div>
);

export function ShopCheckout() {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [f, setF] = useState({ recipient_name: "", recipient_phone: "", shipping_address: "",
    shipping_city: "", shipping_postal_code: "", note: "", referral_code: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authAfterSubmit, setAuthAfterSubmit] = useState(false);
  const shopAutoSubmitRef = useRef(false);
  const [refInfo, setRefInfo] = useState(null);

  useEffect(() => {
    try { setCart(JSON.parse(localStorage.getItem("rsh_cart")) || []); } catch { setCart([]); }
    const ref = new URLSearchParams(window.location.search).get("ref")
      || localStorage.getItem("rsh_ref") || "";
    if (ref) setF((x) => ({ ...x, referral_code: ref.trim().toUpperCase() }));
  }, []);
  useEffect(() => {
    if (user) {
      const addr = [user.address_street, user.address_city].filter(Boolean).join(", ");
      setF((x) => ({
        ...x,
        recipient_name: x.recipient_name || user.full_name || "",
        recipient_phone: x.recipient_phone || user.phone || "",
        shipping_address: x.shipping_address || addr || "",
        shipping_city: x.shipping_city || user.address_city || "",
        shipping_postal_code: x.shipping_postal_code || user.address_postal_code || "",
      }));
    }
  }, [user]);

  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);

  const validateShopRef = useCallback(async (silent = false) => {
    const code = (f.referral_code || "").trim().toUpperCase();
    if (!code) { setRefInfo(null); return true; }
    try {
      const { data } = await api.get(`/public/referral/${code}`);
      setRefInfo(data);
      localStorage.setItem("rsh_ref", code);
      if (!silent) toast.success(`Referral valid: ${data.partner_name}`);
      return true;
    } catch (e) {
      setRefInfo(false);
      if (!silent) toast.error(apiError(e));
      return false;
    }
  }, [f.referral_code]);

  const copyPaymentText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} disalin`);
    } catch {
      toast.error("Gagal menyalin. Silakan salin manual.");
    }
  };

  const openAuth = (mode = "login", afterSubmit = false) => {
    shopAutoSubmitRef.current = afterSubmit;
    setAuthMode(mode);
    setAuthAfterSubmit(afterSubmit);
    setShowAuth(true);
  };

  const createOrder = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop/orders", {
        items: cart.map((c) => ({ product_id: c.id, qty: c.qty })),
        ...f,
        referral_code: (f.referral_code || "").trim().toUpperCase(),
      });
      localStorage.removeItem("rsh_cart");
      setResult(data);
      toast.success(`Order ${data.order.order_number} dibuat`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }, [cart, f]);

  useEffect(() => {
    if (user && authAfterSubmit && shopAutoSubmitRef.current && !result && cart.length > 0) {
      shopAutoSubmitRef.current = false;
      setAuthAfterSubmit(false);
      createOrder();
    }
  }, [user, authAfterSubmit, result, cart.length, createOrder]);

  const submit = async () => {
    if (!(await validateShopRef(true))) {
      toast.error("Kode referral tidak valid. Periksa kembali atau kosongkan kolom referral.");
      return;
    }
    if (!user) return openAuth("register", true);
    return createOrder();
  };

  if (result) return (
    <SiteLayout>
      <div className="mx-auto max-w-2xl px-6 py-16" data-testid="order-success">
        <h1 className="font-display text-3xl font-bold text-[#141414]">Order Berhasil</h1>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Info k="Nomor Order" v={result.order.order_number} testid="result-order-number" />
          <Info k="Invoice" v={result.invoice.invoice_number} />
          <Info k="Ongkos Kirim" v="Gratis Ongkir" testid="result-shipping" />
          <Info k="Total" v={rupiah(result.invoice.total)} testid="result-order-total" />
        </div>
        {result.order.partner_snapshot ? (
          <div className="mt-6 rounded-2xl border border-[#E5DEC9] bg-[#FAF7F0] p-5 text-sm" data-testid="shop-result-referral">
            <div className="eyebrow">Referral Diterapkan</div>
            <div className="mt-2 text-[#4A4844]">
              Consultant: <b className="text-[#141414]">{result.order.partner_snapshot.consultant_name || result.order.partner_snapshot.partner_name}</b>
              <span className="ml-1">({result.order.partner_snapshot.referral_code})</span>
            </div>
            <div className="mt-1 text-xs text-[#8A867C]">
              {result.order.partner_snapshot.branch_name}
              {result.order.partner_snapshot.subbranch_name ? ` · ${result.order.partner_snapshot.subbranch_name}` : ""}
            </div>
          </div>
        ) : null}
        <div className="mt-6 rounded-2xl border border-[#E5DEC9] bg-[#FFFDF5] p-5" data-testid="shop-payment-instructions">
          <div className="eyebrow">Petunjuk Pembayaran</div>
          <p className="mt-2 text-sm text-[#4A4844]">
            Transfer sejumlah <b className="text-[#141414]">{rupiah(result.invoice.total)}</b> ke salah satu rekening berikut,
            lalu cantumkan <b>{result.invoice.invoice_number}</b> atau <b>{result.order.order_number}</b> pada berita transfer.
          </p>
          <div className="mt-4 space-y-3">
            {(result.payment_instructions || []).length > 0 ? result.payment_instructions.map((b, i) => (
              <div key={b.id || b.account_number} className="rounded-xl border border-[#E5DEC9] bg-white p-4" data-testid={`shop-payment-bank-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-[#141414]">{b.bank_name}</div>
                    <div className="mt-1 font-mono text-lg font-bold text-[#121212]" data-testid={`shop-payment-account-number-${i}`}>{b.account_number}</div>
                    <div className="text-xs text-[#8A867C]">a.n. {b.account_holder}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyPaymentText(b.account_number, "Nomor rekening")}
                    data-testid={`shop-copy-bank-account-${i}`}
                    className="flex items-center gap-1 rounded-lg border border-[#E5DEC9] px-3 py-2 text-xs font-semibold text-[#141414] hover:border-[#C5A028] hover:bg-[#F4EFE6]"
                  >
                    <Copy size={13} /> Salin
                  </button>
                </div>
              </div>
            )) : (
              <div className="rounded-xl border border-[#E5DEC9] bg-white p-4 text-sm text-[#8A867C]" data-testid="shop-payment-bank-empty">
                Rekening tujuan belum tersedia. Silakan hubungi admin Royal Saudi Haramain.
              </div>
            )}
          </div>
          <ol className="mt-4 list-decimal space-y-1 pl-5 text-xs text-[#4A4844]" data-testid="shop-payment-steps">
            <li>Simpan nomor order dan invoice di atas.</li>
            <li>Upload bukti transfer melalui tombol di bawah agar Finance dapat memverifikasi pembayaran.</li>
            <li>Pesanan akan diproses setelah pembayaran dikonfirmasi.</li>
          </ol>
        </div>
        <Link to="/portal/tagihan" data-testid="shop-upload-payment-proof" className="btn-gold mt-7 inline-block rounded-xl px-6 py-3 text-sm font-bold">Upload Bukti Bayar</Link>
      </div>
    </SiteLayout>);

  if (cart.length === 0) return <SiteLayout><div className="mx-auto max-w-2xl px-6 py-16"><Empty label="Keranjang kosong." /></div></SiteLayout>;

  return (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-display text-3xl font-bold text-[#141414]">Checkout Toko Online</h1>
        <div className="mt-6 rounded-2xl border border-[#E5DEC9] bg-white p-6" data-testid="checkout-cart">
          {cart.map((c) => (
            <div key={c.id} className="flex justify-between border-b border-[#F4EFE6] py-2 text-sm">
              <span>{c.name} × {c.qty}</span><span>{rupiah(c.price * c.qty)}</span>
            </div>))}
          <div className="flex justify-between py-2 text-sm"><span>Ongkos kirim</span><span className="font-bold text-[#15803D]" data-testid="checkout-shipping">Gratis Ongkir</span></div>
          <div className="flex justify-between pt-2 font-bold text-[#141414]"><span>Total</span><span data-testid="checkout-total">{rupiah(total)}</span></div>
        </div>
        <div className="mt-6 grid gap-4 rounded-2xl border border-[#E5DEC9] bg-white p-6 sm:grid-cols-2">
          {[["recipient_name", "Nama penerima"], ["recipient_phone", "Nomor telepon"],
            ["shipping_city", "Kota"], ["shipping_postal_code", "Kode pos"]].map(([k, label]) => (
            <div key={k}><label className={lbl}>{label}</label>
              <input className={inp} data-testid={`co-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>))}
          <div className="sm:col-span-2"><label className={lbl}>Alamat lengkap</label>
            <textarea rows={2} className={inp} data-testid="co-address" value={f.shipping_address} onChange={(e) => setF({ ...f, shipping_address: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Kode referral (opsional)</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input className={inp} data-testid="co-referral" value={f.referral_code}
                onChange={(e) => { setRefInfo(null); setF({ ...f, referral_code: e.target.value.toUpperCase() }); }}
                onBlur={() => validateShopRef(true)} placeholder="Contoh: UC-XXXXXX" />
              <button type="button" onClick={() => validateShopRef(false)} data-testid="co-referral-check"
                className="rounded-xl border border-[#141414] px-4 py-3 text-sm font-semibold text-[#141414] hover:bg-[#F4EFE6]">Validasi</button>
            </div>
            {refInfo && <div className="mt-2 rounded-lg bg-[#F5F1E8] p-3 text-xs text-[#4A4844]" data-testid="co-referral-valid">
              Referral valid: <b>{refInfo.partner_name}</b> · {refInfo.branch_name}{refInfo.subbranch_name ? ` · ${refInfo.subbranch_name}` : ""}
            </div>}
            {refInfo === false && <div className="mt-2 rounded-lg bg-[#FBE9E9] p-3 text-xs text-[#B91C1C]" data-testid="co-referral-invalid">Kode referral tidak ditemukan atau tidak aktif.</div>}
          </div>
        </div>
        {!user && (
          <div className="mt-6 rounded-xl border border-[#E5DEC9] bg-[#FFFDF5] p-4 text-sm text-[#8C6D1F]" data-testid="shop-inline-auth-notice">
            Akun diperlukan untuk menyimpan invoice dan memantau pengiriman. Anda bisa masuk atau daftar tanpa meninggalkan checkout.
          </div>
        )}
        <button onClick={submit} disabled={busy} data-testid="co-submit" className="btn-gold mt-6 rounded-xl px-7 py-3 text-sm font-bold">
          {busy ? "Memproses..." : user ? "Buat Order & Invoice" : "Masuk/Daftar & Buat Invoice"}
        </button>
      </div>
      {showAuth && (
        <BookingAuthModal
          mode={authMode}
          onModeChange={setAuthMode}
          onClose={() => { shopAutoSubmitRef.current = false; setShowAuth(false); setAuthAfterSubmit(false); }}
          onSuccess={() => setShowAuth(false)}
          initialData={f}
          contextLabel="Lanjutkan Pesanan"
          description="Data keranjang dan alamat pengiriman tetap tersimpan di halaman checkout ini."
          registerSuccessMessage="Akun berhasil dibuat. Pesanan dilanjutkan di halaman ini."
        />
      )}
    </SiteLayout>
  );
}

export function ReferralCapture() {
  const [copied, setCopied] = useState(false);
  const code = new URLSearchParams(window.location.search).get("ref");
  useEffect(() => { if (code) localStorage.setItem("rsh_ref", code); }, [code]);
  return copied ? <Copy size={12} /> : null;
}
